import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { salesOrderService } from '../../services/salesOrderService';
import type { SalesOrderListItemDto } from '../../types/salesOrder.types';
import { useSalesOrderWorkspaceStore } from '../../store/salesOrderWorkspaceStore';

interface Props {
  selectedId: number | null;
  onSelect: (id: number) => void;
}

// Single-row chip strip above the detail panel. Shows up to 5 pinned sales
// orders for quick return — click a chip to swap the active row, X to unpin.
// State (pinnedIds[]) lives in the workspace store; this component just renders
// + resolves labels by id. Mirrors QuotationPinnedStrip.
//
// Labels are resolved per-id via getRow (cheap at the 5-pin cap and works
// regardless of the row's status/archive state, unlike a status-filtered list
// fetch). Hidden when there are no pins so the detail panel gets the full
// vertical space in the common case.

export function SalesOrderPinnedStrip({ selectedId, onSelect }: Props) {
  const pinnedIds = useSalesOrderWorkspaceStore((s) => s.pinnedIds);
  const togglePin = useSalesOrderWorkspaceStore((s) => s.togglePin);
  const [labels, setLabels] = useState<Map<number, SalesOrderListItemDto>>(new Map());

  useEffect(() => {
    if (pinnedIds.length === 0) {
      setLabels(new Map());
      return;
    }
    let cancelled = false;
    Promise.all(
      pinnedIds.map((id) =>
        salesOrderService
          .getRow(id)
          .then((row) => [id, row] as const)
          .catch(() => null),
      ),
    ).then((entries) => {
      if (cancelled) return;
      const map = new Map<number, SalesOrderListItemDto>();
      for (const entry of entries) {
        if (entry) map.set(entry[0], entry[1]);
      }
      setLabels(map);
    });
    return () => {
      cancelled = true;
    };
  }, [pinnedIds]);

  if (pinnedIds.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-1 border-b bg-muted/30 px-3 py-2">
      <span className="mr-1 text-[11px] uppercase tracking-wider text-muted-foreground">
        Pinned
      </span>
      {pinnedIds.map((id) => {
        const item = labels.get(id);
        const label = item
          ? item.customerName || item.projectName || item.salesOrderNumber
          : `#${id}`;
        const active = id === selectedId;
        return (
          <div
            key={id}
            className={cn(
              'group flex items-center gap-1 rounded-md border px-2 py-1 text-xs transition-colors',
              active
                ? 'border-primary bg-primary/10 text-foreground'
                : 'bg-background hover:bg-accent',
            )}
          >
            <button
              type="button"
              onClick={() => onSelect(id)}
              className="max-w-[180px] truncate text-left font-medium"
              title={label}
            >
              {label}
            </button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-4 w-4 shrink-0 p-0 opacity-50 hover:opacity-100"
              onClick={(e) => {
                e.stopPropagation();
                togglePin(id);
              }}
              aria-label="Unpin"
              title="Unpin"
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        );
      })}
    </div>
  );
}
